import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Shield, Plus, X, RefreshCw, KeyRound, Power, Trash2, Mail, Send } from 'lucide-react';
import { useAdminDelete } from '../hooks/useAdminDelete';
import { ConfirmDeleteModal } from '../components/ConfirmDeleteModal';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function AdminPortalAccounts() {
  const [accounts, setAccounts] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ client_id: '', email: '', password: '', name: '', reminder_days: 15 });
  const [sending, setSending] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [a, c] = await Promise.all([
        axios.get(`${API}/portal/admin/accounts`, { withCredentials: true }),
        axios.get(`${API}/clients/`, { withCredentials: true }),
      ]);
      setAccounts(a.data || []);
      setClients((c.data || []).filter(x => ['Debtor', 'Both'].includes(x.type)));
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.client_id || !form.email || !form.password) return toast.error('Fill all required fields');
    try {
      await axios.post(`${API}/portal/admin/accounts`, form, { withCredentials: true });
      toast.success('Portal login created');
      setShowNew(false);
      setForm({ client_id: '', email: '', password: '', name: '', reminder_days: 15 });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const resetPwd = async (acc) => {
    const np = window.prompt(`New password for ${acc.email}:`);
    if (!np) return;
    try {
      await axios.put(`${API}/portal/admin/accounts/${acc.id}`, { password: np }, { withCredentials: true });
      toast.success('Password updated');
    } catch { toast.error('Failed'); }
  };

  const toggleActive = async (acc) => {
    try {
      await axios.put(`${API}/portal/admin/accounts/${acc.id}`, { is_active: !acc.is_active }, { withCredentials: true });
      toast(acc.is_active ? 'Account disabled' : 'Account enabled');
      load();
    } catch { toast.error('Failed'); }
  };

  const setReminderDays = async (acc) => {
    const cur = clients.find(c => c.id === acc.client_id)?.reminder_days || 15;
    const val = window.prompt(`Reminder window in days (current ${cur}):`, String(cur));
    const n = parseInt(val, 10);
    if (!n || n < 1 || n > 120) return;
    try {
      await axios.put(`${API}/portal/admin/accounts/${acc.id}`, { reminder_days: n, client_id: acc.client_id }, { withCredentials: true });
      toast.success(`Reminder set to ${n} days`);
      load();
    } catch { toast.error('Failed'); }
  };

  const { deleteState, requestDelete, confirmDelete, cancelDelete } = useAdminDelete(load);

  const remove = (acc) => {
    requestDelete(
      `/portal/admin/accounts/${acc.id}`,
      acc.email || 'this account',
      `This will permanently delete the portal login for ${acc.email}. The client's data is not affected.`,
    );
  };

  const runReminders = async () => {
    setSending(true);
    try {
      const res = await axios.post(`${API}/portal/admin/send-reminders`, {}, { withCredentials: true });
      toast.success(`Sent ${res.data?.emails_sent || 0} email(s), ${res.data?.pushes_sent || 0} push(es)`);
    } catch { toast.error('Failed to send reminders'); }
    finally { setSending(false); }
  };

  const clientName = (id) => clients.find(c => c.id === id)?.name || '—';
  const reminderOf = (id) => clients.find(c => c.id === id)?.reminder_days || 15;

  return (
    <div className="space-y-5" data-testid="admin-portal-accounts">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl font-semibold text-zinc-50 flex items-center gap-2">
            <Shield size={18} className="text-amber-400" /> Client Portal Accounts
          </h1>
          <p className="text-zinc-500 text-sm mt-1">Manage logins, reset passwords, configure overdue-bill reminder windows.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={runReminders} disabled={sending} data-testid="run-reminders-btn"
            className="flex items-center gap-1.5 bg-zinc-800 border border-zinc-700 hover:bg-zinc-700 text-zinc-200 text-xs px-3 py-2 rounded-lg disabled:opacity-50">
            {sending ? <RefreshCw size={11} className="animate-spin" /> : <Send size={11} />} Run Overdue Reminders
          </button>
          <button onClick={() => setShowNew(true)} data-testid="new-portal-acct-btn"
            className="flex items-center gap-1.5 bg-amber-500 hover:bg-amber-400 text-black text-xs font-semibold px-3 py-2 rounded-lg">
            <Plus size={11} /> New Login
          </button>
        </div>
      </div>

      <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-zinc-800">
            {['Client', 'Login Email', 'Reminder', 'Status', 'Actions'].map(h => (
              <th key={h} className="text-left text-zinc-500 text-xs font-medium px-4 py-2.5">{h}</th>
            ))}
          </tr></thead>
          <tbody>
            {loading && <tr><td colSpan={5} className="p-6 text-center text-zinc-500 text-sm">Loading…</td></tr>}
            {!loading && accounts.length === 0 && <tr><td colSpan={5} className="p-10 text-center text-zinc-500 text-sm">No portal accounts yet. Click New Login to create one.</td></tr>}
            {accounts.map(a => (
              <tr key={a.id} className="border-b border-zinc-800/50 hover:bg-zinc-800/30">
                <td className="px-4 py-3 text-zinc-200 text-xs font-medium">{clientName(a.client_id)}</td>
                <td className="px-4 py-3 text-zinc-400 text-xs font-mono">{a.email}</td>
                <td className="px-4 py-3"><button onClick={() => setReminderDays(a)} className="text-[11px] text-amber-300 hover:underline">{reminderOf(a.client_id)} days</button></td>
                <td className="px-4 py-3">
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${a.is_active === false ? 'bg-red-900/40 text-red-300' : 'bg-green-900/40 text-green-300'}`}>
                    {a.is_active === false ? 'Disabled' : 'Active'}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1.5">
                    <button onClick={() => resetPwd(a)} className="p-1.5 rounded bg-zinc-800 text-zinc-400 hover:text-amber-300" title="Reset Password"><KeyRound size={12} /></button>
                    <button onClick={() => toggleActive(a)} className="p-1.5 rounded bg-zinc-800 text-zinc-400 hover:text-amber-300" title={a.is_active === false ? 'Enable' : 'Disable'}><Power size={12} /></button>
                    <button onClick={() => remove(a)} className="p-1.5 rounded bg-red-900/20 text-red-400 hover:text-red-300" title="Delete"><Trash2 size={12} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* New Account Modal */}
      {showNew && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-950 border border-zinc-800 rounded-2xl w-full max-w-md" data-testid="new-portal-acct-modal">
            <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-800">
              <h3 className="font-heading font-semibold text-zinc-50 text-sm">Create Portal Login</h3>
              <button onClick={() => setShowNew(false)} className="text-zinc-500 hover:text-zinc-200"><X size={16} /></button>
            </div>
            <div className="p-5 space-y-3">
              <div>
                <label className="text-zinc-400 text-xs mb-1 block">Client</label>
                <select value={form.client_id} onChange={e => setForm(f => ({ ...f, client_id: e.target.value }))}
                  data-testid="pa-client-select"
                  className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-zinc-50 text-sm">
                  <option value="">Select a debtor…</option>
                  {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-zinc-400 text-xs mb-1 block">Login Email</label>
                <input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                  data-testid="pa-email" placeholder="client@example.com"
                  className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-zinc-50 text-sm" />
              </div>
              <div>
                <label className="text-zinc-400 text-xs mb-1 block">Temporary Password</label>
                <input type="text" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                  data-testid="pa-password"
                  className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-zinc-50 text-sm font-mono" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-zinc-400 text-xs mb-1 block">Display Name</label>
                  <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                    className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-zinc-50 text-sm" />
                </div>
                <div>
                  <label className="text-zinc-400 text-xs mb-1 block">Reminder Window (days)</label>
                  <input type="number" min="1" max="120" value={form.reminder_days}
                    data-testid="pa-reminder-days"
                    onChange={e => setForm(f => ({ ...f, reminder_days: parseInt(e.target.value || '15', 10) }))}
                    className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-zinc-50 text-sm" />
                </div>
              </div>
              <div className="text-[11px] text-zinc-500 flex items-center gap-1.5 bg-zinc-900/50 rounded p-2">
                <Mail size={11} /> Share these credentials with the client over a secure channel.
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button onClick={() => setShowNew(false)} className="px-4 py-2 bg-zinc-800 text-zinc-300 rounded-lg text-xs">Cancel</button>
                <button onClick={create} data-testid="pa-create-btn"
                  className="px-4 py-2 bg-amber-500 hover:bg-amber-400 text-black text-xs font-semibold rounded-lg">Create Login</button>
              </div>
            </div>
          </div>
        </div>
      )}
      <ConfirmDeleteModal
        open={deleteState.open}
        onClose={cancelDelete}
        onConfirm={confirmDelete}
        title={deleteState.title}
        message={deleteState.message}
      />
    </div>
  );
}
